/**
 * Device class and OS, read once per resize and mirrored onto <html> as
 * data-device / data-os so CSS can key off them without prop-drilling.
 *
 * Breakpoints are viewport width, not device name — a folded-out tablet and a
 * small laptop should get the same layout. "device" only says how much room
 * there is to lay things out in; "os" is the separate, narrower question of
 * platform-specific behaviour (safe-area insets, the iOS install prompt).
 */
import { useEffect, useState } from 'react'

const TABLET_MIN = 768
const DESKTOP_MIN = 1024

function detectOS() {
  if (typeof navigator === 'undefined') return 'other'
  const ua = navigator.userAgent || ''
  const platform = navigator.platform || ''
  const maxTouch = navigator.maxTouchPoints || 0
  // iPadOS 13+ identifies as "MacIntel" in the UA string — the only thing
  // that still gives it away is that a real Mac has no touch points.
  if (/iPad|iPhone|iPod/.test(ua) || (platform === 'MacIntel' && maxTouch > 1)) return 'ios'
  if (/Android/.test(ua)) return 'android'
  return 'other'
}

function deviceForWidth(w) {
  if (w >= DESKTOP_MIN) return 'desktop'
  if (w >= TABLET_MIN) return 'tablet'
  return 'phone'
}

export function isStandalone() {
  if (typeof window === 'undefined') return false
  return window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true
}

const OS = detectOS() // fixed for the life of the tab — the OS doesn't change under you

export function usePlatform() {
  const [width, setWidth] = useState(() => (typeof window === 'undefined' ? DESKTOP_MIN : window.innerWidth))
  const [touch, setTouch] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia?.('(pointer:coarse)').matches
  )

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth)
    window.addEventListener('resize', onResize)
    const mq = window.matchMedia('(pointer:coarse)')
    const onPointer = () => setTouch(mq.matches)
    mq.addEventListener?.('change', onPointer)
    return () => {
      window.removeEventListener('resize', onResize)
      mq.removeEventListener?.('change', onPointer)
    }
  }, [])

  const device = deviceForWidth(width)

  // Stamped on every call site that stays mounted for the app's life (Shell,
  // on every screen) rather than in one top-level effect — colocating the
  // side effect with the hook means any consumer keeps the attributes fresh,
  // and there's exactly one hook to reason about, not a hook plus a provider.
  useEffect(() => {
    document.documentElement.dataset.device = device
    document.documentElement.dataset.os = OS
  }, [device])

  return { device, os: OS, isTouch: touch, width }
}
